import React from "react";
import { view } from "../../lib/vvm";
import { Layout } from "../../components/Layout/Layout";
import Button, { ButtonDesign } from "../../components/Button/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialog";
import { UsersManagementPageViewModel } from "./UsersManagementPageViewModel";
import styles from "./UsersManagementPage.module.css";

export const UsersManagementPage: React.FC = view(UsersManagementPageViewModel)(({
  viewModel: {
    isAdmin,
    usersForView,
    pendingUsersCount,
    pageSubtitle,
    isLoading,
    error,
    isDeleteDialogOpen,
    deleteDialogTitle,
    deleteDialogDescription,
    goBack,
    refresh,
    approveUser,
    requestDeleteUser,
    cancelDeleteUser,
    confirmDeleteUser,
    formatLastActive,
    isUserProcessing,
  },
}) => {
  return (
    <Layout title={"Пользователи"}>
      <section className={styles.root}>
        <div className={styles.toolbar}>
          <div className={styles.titleBlock}>
            <strong className={styles.title}>Управление пользователями</strong>
            <span className={styles.subtitle}>{pageSubtitle}</span>
          </div>
          <Button
            design={ButtonDesign.ghost}
            className={styles.refresh}
            onClick={() => void refresh()}
            disabled={isLoading}
          >
            обновить
          </Button>
        </div>

        {!isAdmin ? (
          <div className={styles.stateCard}>
            <strong>Недостаточно прав</strong>
            <span>Раздел доступен только администратору</span>
            <Button design={ButtonDesign.basic} onClick={goBack}>
              на главную
            </Button>
          </div>
        ) : (
          <>
            {pendingUsersCount > 0 && (
              <div className={styles.pendingBanner}>
                Новые заявки на доступ: {pendingUsersCount}
              </div>
            )}

            {error && <div className={styles.error}>{error}</div>}

            {isLoading ? (
              <div className={styles.stateCard}>
                <strong>Загружаем пользователей</strong>
                <span>Обновляем список и статусы заявок</span>
              </div>
            ) : usersForView.length === 0 ? (
              <div className={styles.stateCard}>
                <strong>Пользователи не найдены</strong>
              </div>
            ) : (
              <ul className={styles.list}>
                {usersForView.map((user) => {
                  const isProcessing = isUserProcessing(user.id);
                  const isPending = user.status === "pending";

                  return (
                    <li key={user.id} className={styles.item}>
                      <article className={styles.card}>
                        <div className={styles.cardHeader}>
                          <div className={styles.nameBlock}>
                            <strong className={styles.name}>{user.name}</strong>
                            <span className={styles.id}>{user.id}</span>
                          </div>
                          <span
                            className={`${styles.status} ${
                              isPending ? styles.statusPending : styles.statusActive
                            }`}
                          >
                            {isPending ? "ожидает одобрения" : "активен"}
                          </span>
                        </div>

                        <div className={styles.metaRow}>
                          <span className={styles.meta}>роль: {user.role}</span>
                          <span className={styles.meta}>
                            активность: {formatLastActive(user.lastActive)}
                          </span>
                        </div>

                        <div className={styles.actions}>
                          <Button
                            design={ButtonDesign.basic}
                            onClick={() => void approveUser(user)}
                            disabled={isProcessing || !isPending}
                            isLoading={isProcessing && isPending}
                          >
                            одобрить
                          </Button>
                          <Button
                            design={ButtonDesign.basic}
                            onClick={() => requestDeleteUser(user)}
                            disabled={isProcessing}
                          >
                            удалить
                          </Button>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </section>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={deleteDialogTitle}
        description={deleteDialogDescription}
        tone={"danger"}
        confirmLabel={"удалить"}
        cancelLabel={"отмена"}
        onCancel={cancelDeleteUser}
        onConfirm={() => void confirmDeleteUser()}
      />
    </Layout>
  );
});
